<template>
  <div id="app" :class="{ 'dark-mode': isDarkMode }">
    <!-- 载入动画 -->
    <LoadingAnimation v-if="showLoadingAnimation" @loading-complete="onLoadingComplete" />
    
    <div class="body-wrapper" v-show="!showLoadingAnimation">
    <!-- Preloader Start -->
    <div id="preloader" v-if="isLoading">
      <div class="loader3">
        <span></span>
        <span></span>
      </div>
    </div>
    <!-- Preloader End -->

    <!-- Day and Night Mode Toggle -->
    <div class="toggle-cont">
      <input type="checkbox" class="toggle-input" id="toggle" v-model="isDarkMode">
      <label for="toggle" class="toggle-label">
        <div class="cont-icon">
          <!-- 太阳图标 -->
          <svg class="icon" viewBox="0 0 24 24" v-show="!isDarkMode">
            <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
          </svg>
          <!-- 月亮图标 -->
          <svg class="icon" viewBox="0 0 24 24" v-show="isDarkMode">
            <path d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"/>
          </svg>
          <!-- 星星效果 -->
          <div class="sparkle" style="--deg: 0; --duration: 3; --width: 2;"></div>
          <div class="sparkle" style="--deg: 45; --duration: 4; --width: 1.5;"></div>
          <div class="sparkle" style="--deg: 90; --duration: 2.5; --width: 2.5;"></div>
          <div class="sparkle" style="--deg: 135; --duration: 3.5; --width: 1.8;"></div>
          <div class="sparkle" style="--deg: 180; --duration: 2.8; --width: 2.2;"></div>
          <div class="sparkle" style="--deg: 225; --duration: 4.2; --width: 1.6;"></div>
          <div class="sparkle" style="--deg: 270; --duration: 3.2; --width: 2.4;"></div>
          <div class="sparkle" style="--deg: 315; --duration: 2.6; --width: 1.9;"></div>
        </div>
      </label>
    </div>

    <!-- Menu Toggler -->
    <div class="hamburger-menu" @click="toggleSidebar" :class="{ active: isSidebarOpen }">
      <span class="line-top"></span>
      <span class="line-center"></span>
      <span class="line-bottom"></span>
    </div>

    <!-- 移动端遮罩层 -->
    <div 
      v-if="isSidebarOpen && isMobile" 
      class="sidebar-overlay"
      @click="toggleSidebar"
    ></div>
    
    <!-- Sidebar -->
    <div class="side-bar" :class="{ open: isSidebarOpen, collapsed: isSidebarCollapsed && !isMobile }">
      <div class="sidebar-content">
        <div class="user-info">
          <div class="user-img" @click="!isMobile && toggleSidebarCollapse()" :style="{ cursor: isMobile ? 'default' : 'pointer' }">
            <img src="@/assets/img/user/avatar.png" alt="用户头像">
          </div>
          <div class="user-text" v-show="!isSidebarCollapsed">
            <h2 class="name gradient-text" data-glitch="xuxuya66">xuxuya66</h2>
            <p class="greeting btn-shine">Just Do It!</p>
          </div>
        </div>

        <div class="menu">
          <ul class="menu-ul">
            <li class="menu-list" v-for="(item, index) in menuItems" :key="index">
              <router-link :to="item.path" class="menu-link" :title="isSidebarCollapsed ? item.title : ''">
                <el-icon><component :is="item.icon"></component></el-icon>
                <span v-show="!isSidebarCollapsed">{{ item.title }}</span>
              </router-link>
            </li>
          </ul>
        </div>

        <div class="social" v-show="!isSidebarCollapsed">
          <ul class="social-ul">
            <li class="social-list" v-for="(item, index) in socialLinks" :key="index">
              <a :href="item.url" class="social-link"><el-icon><component :is="item.icon"></component></el-icon></a>
            </li>
          </ul>
        </div>
        
        <!-- 收缩按钮移到底部 - 只在桌面端显示 -->
        <div 
          class="collapse-btn" 
          @click="toggleSidebarCollapse" 
          v-show="isSidebarOpen && !isMobile"
          :data-tooltip="isSidebarCollapsed ? '展开侧边栏' : '收缩侧边栏'"
        >
          <el-icon>
            <component :is="isSidebarCollapsed ? 'ArrowRight' : 'ArrowLeft'"></component>
          </el-icon>
        </div>
      </div>
    </div>

    <!-- Content Wrapper -->
    <div class="content-wrapper">
      <router-view></router-view>
    </div>
    </div>
  </div>
</template>

<script>
import { House, Document, Briefcase, Reading, Message, Share, Camera, Monitor, Picture, ChatDotRound, ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import LoadingAnimation from '@/components/LoadingAnimation.vue'

export default {
  components: {
    House, Document, Briefcase, Reading, Message, Share, Camera, Monitor, Picture, ChatDotRound, ArrowLeft, ArrowRight,
    LoadingAnimation
  },
  name: 'App',
  data() {
    return {
      showLoadingAnimation: true, // 控制载入动画显示
      isLoading: true,
      isSidebarOpen: false, // 由 mounted 钩子初始化
      isSidebarCollapsed: false, // 侧边栏收缩状态
      isDarkMode: false,
      isMobile: false, // 由 mounted 钩子初始化
      menuItems: [
        { title: '首页', path: '/', icon: 'House' },
        { title: '简历', path: '/resume', icon: 'Document' },
        { title: '作品', path: '/work', icon: 'Briefcase' },
        { title: '博客', path: '/stories', icon: 'Reading' },
        { title: '联系', path: '/contact', icon: 'Message' }
      ],
      socialLinks: [
        { icon: 'Share', url: '#' },
        { icon: 'Camera', url: '#' },
        { icon: 'Monitor', url: '#' },
        { icon: 'Picture', url: '#' },
        { icon: 'ChatDotRound', url: '#' }
      ]
    }
  },
  watch: {
    isDarkMode(newValue) {
      if (newValue) {
        document.documentElement.classList.add('dark-mode')
        localStorage.setItem('darkMode', 'true')
      } else {
        document.documentElement.classList.remove('dark-mode')
        localStorage.setItem('darkMode', 'false')
      }
    }
  },
  mounted() {
    // 模拟加载完成
    setTimeout(() => {
      this.isLoading = false
    }, 1000)

    // 检查本地存储中是否有暗色模式设置
    const darkMode = localStorage.getItem('darkMode')
    if (darkMode === 'true') {
      this.isDarkMode = true
      document.documentElement.classList.add('dark-mode')
    }

    // 初始化状态
    this.handleResize();

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    // 清理事件监听器
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 1200;

      // 仅在从桌面切换到移动或反之时，才重置侧边栏状态
      if (wasMobile !== this.isMobile) {
        this.isSidebarOpen = !this.isMobile;
      }
    },
    toggleSidebar() {
      console.log('Toggle sidebar clicked, current state:', this.isSidebarOpen); // 调试日志
      this.isSidebarOpen = !this.isSidebarOpen
      console.log('New sidebar state:', this.isSidebarOpen); // 调试日志
      
      // 移动端点击侧边栏外部区域关闭侧边栏
      if (this.isSidebarOpen && this.isMobile) {
        this.$nextTick(() => {
          document.addEventListener('click', this.handleOutsideClick, { once: true })
        })
      }
    },
    handleOutsideClick(event) {
      const sidebar = document.querySelector('.side-bar')
      const hamburger = document.querySelector('.hamburger-menu')
      
      if (sidebar && hamburger && 
          !sidebar.contains(event.target) && 
          !hamburger.contains(event.target)) {
        this.isSidebarOpen = false
      }
    },
    toggleSidebarCollapse() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode
    },
    onLoadingComplete() {
      // 载入动画完成后的回调
      this.showLoadingAnimation = false;
      console.log('Loading animation completed');
    }
  }
}
</script>

<style>
/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 日夜模式CSS变量 */
:root {
  /* 白天模式 */
  --bg-primary: #f8f9fa;
  --bg-secondary: #ffffff;
  --bg-tertiary: #f5f7fa;
  --text-primary: #333333;
  --text-secondary: #666666;
  --text-muted: #999999;
  --border-color: #e9ecef;
  --shadow-light: rgba(0, 0, 0, 0.1);
  --shadow-medium: rgba(0, 0, 0, 0.15);
  --card-bg: #ffffff;
  --sidebar-bg: #2c3e50;
  --gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --gradient-secondary: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* 夜晚模式 */
.dark-mode {
  --bg-primary: #1a1a1a;
  --bg-secondary: #2d2d2d;
  --bg-tertiary: #3a3a3a;
  --text-primary: #e0e0e0;
  --text-secondary: #b0b0b0;
  --text-muted: #888888;
  --border-color: #404040;
  --shadow-light: rgba(0, 0, 0, 0.3);
  --shadow-medium: rgba(0, 0, 0, 0.5);
  --card-bg: #2d2d2d;
  --sidebar-bg: #1e1e1e;
  --sidebar-border: #333333;
  --sidebar-text: #ffffff;
  --sidebar-text-secondary: #cccccc;
  --sidebar-hover: rgba(255, 255, 255, 0.15);
  --gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --gradient-secondary: linear-gradient(135deg, #2d2d2d 0%, #3a3a3a 100%);
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  line-height: 1.6;
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* 全局样式可以放在这里，或者在main.js中导入 */
.gradient-text {
  position: relative;
  font-size: 25px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  letter-spacing: 5px;
  z-index: 1;
  animation: shift 1s ease-in-out infinite alternate;
}

.gradient-text::before,
.gradient-text::after {
  display: block;
  content: attr(data-glitch);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.8;
}

.gradient-text::before {
  animation: glitch-1 0.5s infinite;
  color: #00fffc;
  z-index: -1;
}

.gradient-text::after {
  animation: glitch-2 0.5s infinite;
  color: #fc00ff;
  z-index: -2;
}

@keyframes glitch-1 {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-2px);
  }
  40% {
    transform: translateX(-2px);
  }
  60% {
    transform: translateX(2px);
  }
  80% {
    transform: translateX(2px);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes glitch-2 {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(2px);
  }
  40% {
    transform: translateX(2px);
  }
  60% {
    transform: translateX(-2px);
  }
  80% {
    transform: translateX(-2px);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes glitch {
  0% {
    transform: translate(0);
  }

  20% {
    transform: translate(-3px, 3px);
  }

  40% {
    transform: translate(-3px, -3px);
  }

  60% {
    transform: translate(3px, 3px);
  }

  80% {
    transform: translate(3px, -3px);
  }

  to {
    transform: translate(0);
  }
}

@keyframes shift {
  0%,
  40%,
  44%,
  58%,
  61%,
  65%,
  69%,
  73%,
  100% {
    transform: skewX(0deg);
  }
  41% {
    transform: skewX(10deg);
  }
  42% {
    transform: skewX(-10deg);
  }
  59% {
    transform: skewX(40deg) skewY(10deg);
  }
  60% {
    transform: skewX(-40deg) skewY(-10deg);
  }
  63% {
    transform: skewX(10deg) skewY(-5deg);
  }
  70% {
    transform: skewX(-50deg) skewY(-20deg);
  }
  71% {
    transform: skewX(10deg) skewY(-10deg);
  }
}

.btn-shine {
  position: relative;
  padding: 12px 48px;
  color: #fff;
  background: linear-gradient(to right, #2a2a2a 0, #ffffff 10%, #1a1a1a 20%);
  background-position: 0;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: shine 2s infinite linear;
  animation-fill-mode: forwards;
  -webkit-text-size-adjust: none;
  font-weight: 600 !important;
  font-size: 22px !important;
  text-decoration: none;
  white-space: nowrap;
  font-family: "Poppins", sans-serif;
}

@-moz-keyframes shine {
  0% {
    background-position: 0;
  }
  60% {
    background-position: 180px;
  }
  100% {
    background-position: 180px;
  }
}

@-webkit-keyframes shine {
  0% {
    background-position: 0;
  }
  60% {
    background-position: 180px;
  }
  100% {
    background-position: 180px;
  }
}

@-o-keyframes shine {
  0% {
    background-position: 0;
  }
  60% {
    background-position: 180px;
  }
  100% {
    background-position: 180px;
  }
}

@keyframes shine {
  0% {
    background-position: 0;
  }
  60% {
    background-position: 180px;
  }
  100% {
    background-position: 180px;
  }
}

/* 自定义滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(45deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(45deg, #f093fb 0%, #f5576c 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transform: scale(1.1);
}

::-webkit-scrollbar-thumb:active {
  background: linear-gradient(45deg, #4facfe 0%, #00f2fe 100%);
}

::-webkit-scrollbar-corner {
  background: transparent;
}

/* Firefox 滚动条样式 */
* {
  scrollbar-width: thin;
  scrollbar-color: #667eea rgba(255, 255, 255, 0.1);
}

/* 夜间模式下的滚动条样式 */
.dark-mode ::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  box-shadow: inset 0 0 6px rgba(255, 255, 255, 0.1);
}

.dark-mode * {
  scrollbar-color: #764ba2 rgba(0, 0, 0, 0.2);
}

/* 主内容区域样式 */
.content-wrapper {
  margin-left: 200px; /* 桌面端默认为侧边栏留出空间 */
  padding: 1rem; /* 减少内边距 */
  min-height: 100vh;
  background-color: var(--bg-primary);
  transition: margin-left 0.3s ease, background-color 0.3s ease, width 0.3s ease;
  width: calc(100% - 200px); /* 确保内容区域宽度正确 */
  box-sizing: border-box; /* 包含padding在内的盒模型 */
}

/* 收缩状态下的内容区域 */
.side-bar.collapsed ~ .content-wrapper {
  margin-left: 70px;
  width: calc(100% - 70px);
}

/* 侧边栏样式 */
.side-bar {
  background: #f7f7f7;
  padding: 40px 0px 0 0;
  border-right: 1px solid #ddd;
  overflow-y: auto; /* 允许垂直滚动 */
  overflow-x: hidden; /* 隐藏水平滚动条 */
  scroll-behavior: smooth; /* 平滑滚动效果 */
  position: fixed;
  transition: transform 0.3s ease; /* 只对transform进行过渡 */
  width: 200px;
  height: 100vh;
  top: 0;
  left: 0;
  z-index: 1001; /* 确保在遮罩层之上 */
  color: var(--sidebar-text, #333);
}

/* 收缩状态的侧边栏 */
.side-bar.collapsed {
  width: 70px;
}

/* 夜间模式下的侧边栏样式 */
.dark-mode .side-bar {
  background: #1e1e1e;
  color: #ffffff;
  border-right: 1px solid #333333;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.5);
}

.side-bar.open {
  left: 0;
}

/* 移动端响应式样式 */
@media screen and (max-width: 1200px) {
  .side-bar {
    transform: translateX(-100%) !important; /* 移动端默认隐藏 */
    box-shadow: 2px 0 15px rgba(0, 0, 0, 0.3);
  }
  
  .side-bar.open {
    transform: translateX(0) !important; /* 移动端打开时显示 */
    box-shadow: 2px 0 25px rgba(0, 0, 0, 0.4);
  }
  
  .side-bar.collapsed {
    transform: translateX(-100%); /* 收缩状态也隐藏 */
    width: 70px;
  }
  
  .side-bar.collapsed.open {
    transform: translateX(0); /* 移动端收缩状态打开时显示 */
  }
  
  .content-wrapper {
    margin-left: 0; /* 移动端内容区域占满宽度 */
    width: 100%; /* 移动端占满全宽 */
    padding: 0.5rem; /* 移动端减少内边距 */
  }
  
  .side-bar.collapsed ~ .content-wrapper {
    margin-left: 0;
    width: 100%;
  }
  
  .collapse-btn {
    display: none; /* 移动端隐藏收缩按钮 */
  }
}

.side-bar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.side-bar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* 侧边栏内容样式 */
.sidebar-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch; /* iOS 平滑滚动 */
}

.sidebar-content::-webkit-scrollbar {
  display: none;
}

.sidebar-content {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* 收缩按钮样式 - 底部版本 */
.collapse-btn {
  position: relative;
  width: 50px;
  height: 50px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px var(--shadow-medium);
  margin: 1rem auto 1rem auto;
}

.collapse-btn:hover {
  background: var(--bg-tertiary);
  transform: scale(1.05);
  box-shadow: 0 6px 16px var(--shadow-medium);
}

.collapse-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px var(--shadow-light);
}

.collapse-btn .el-icon {
  font-size: 22px;
  color: var(--text-primary);
  transition: transform 0.2s ease;
}

.collapse-btn:hover .el-icon {
  transform: scale(1.1);
}

/* 夜间模式下的收缩按钮 */
.dark-mode .collapse-btn {
  background: var(--bg-secondary);
  border-color: var(--border-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.dark-mode .collapse-btn:hover {
  background: var(--bg-tertiary);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.6);
}

/* 添加一个可选的文字提示 */
.collapse-btn::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.collapse-btn:hover::after {
  opacity: 1;
  visibility: visible;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.5rem 0.2rem;
  margin-bottom: 1rem;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

/* 收缩状态下的用户信息 */
.side-bar.collapsed .user-info {
  flex-direction: column;
  text-align: center;
  padding: 0.5rem 0.3rem;
  margin-bottom: 1rem;
  gap: 0.3rem;
}

.user-img {
  width: 65px;
  height: 65px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #667eea;
  transition: all 0.3s ease;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

/* 收缩状态下的用户头像 */
.side-bar.collapsed .user-img {
  width: 55px;
  height: 55px;
  border: 3px solid #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.user-text {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  align-items: center;
}

/* 夜间模式下的用户头像边框 */
.dark-mode .user-img {
  border: 3px solid #667eea;
  box-shadow: 0 0 15px rgba(102, 126, 234, 0.3);
}

.user-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.greeting {
  font-size: 0.75rem;
  color: #888888;
  margin: 0;
  font-weight: 400;
}

.name {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #333333; /* 日间模式下使用深色 */
}

/* 夜间模式下的用户信息样式 */
.dark-mode .greeting {
  color: #aaaaaa;
}

.dark-mode .name {
  color: #ffffff;
}

/* 菜单样式 */
.menu {
  flex: 1;
  margin-top: 0;
}

.menu-ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-list {
  margin-bottom: 0;
}

.menu-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.8rem 1rem;
  color: #666666; /* 日间模式下使用深灰色 */
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  border-radius: 12px;
  margin: 0 0.5rem 0 2rem;
  text-align: left;
  flex-direction: row;
  white-space: nowrap;
  line-height: 1;
  min-height: 40px;
}

/* 收缩状态下的菜单链接 */
.side-bar.collapsed .menu-link {
  justify-content: center;
  padding: 0.8rem;
  text-align: center;
  margin: 0 0.3rem;
  gap: 0;
  flex-direction: row;
  padding-left: 0.8rem;
}

.menu-link:hover {
  background: rgba(102, 126, 234, 0.1); /* 日间模式下使用浅蓝色背景 */
  color: #333333; /* 日间模式下使用深色文字 */
  transform: translateX(5px);
}

/* 收缩状态下的悬停效果 */
.side-bar.collapsed .menu-link:hover {
  transform: scale(1.1);
}

.menu-link.router-link-active {
  background: rgba(102, 126, 234, 0.1); /* 日间模式下使用浅蓝色背景 */
  color: #333333; /* 日间模式下使用深色文字 */
  border-right: 3px solid #667eea;
  margin-left: 0;
  margin-right: 0;
  border-radius: 0;
  padding-left: 3rem;
}

/* 收缩状态下的激活状态 */
.side-bar.collapsed .menu-link.router-link-active {
  border-right: none;
  border-bottom: 3px solid #667eea;
}

/* 夜间模式下的菜单链接样式 */
.dark-mode .menu-link {
  color: #cccccc;
}

.dark-mode .menu-link:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.dark-mode .menu-link.router-link-active {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  border-right: 3px solid #764ba2;
}

/* 夜间模式下收缩状态的激活状态 */
.dark-mode .side-bar.collapsed .menu-link.router-link-active {
  border-right: none;
  border-bottom: 3px solid #764ba2;
}

/* 夜间模式下收缩状态的用户头像 */
.dark-mode .side-bar.collapsed .user-img {
  border-color: #764ba2;
}

/* 菜单图标样式 */
.menu-link .el-icon {
  font-size: 22px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  color: #28a745; /* 改为绿色 */
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

/* 菜单文字样式 */
.menu-link span {
  font-weight: 500;
  letter-spacing: 0.5em;
  display: inline-block;
  line-height: 1;
  vertical-align: middle;
}

/* 收缩状态下的菜单图标 */
.side-bar.collapsed .menu-link .el-icon {
  font-size: 20px;
  width: 20px;
  height: 20px;
  margin-right: 0;
}

/* 夜间模式下的菜单图标 */
.dark-mode .menu-link .el-icon {
  color: #764ba2;
}

/* 社交链接样式 */
.social {
  padding: 1rem 0;
}

.social-ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  max-width: 100%;
}

.social-list {
  margin: 0;
}

.social-link {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background: #667eea;
  border-radius: 50%;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.social-link:hover {
  background: var(--sidebar-text, white);
  color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

.social-link .el-icon {
  font-size: 1.5rem; /* 增大图标尺寸 */
}

/* 夜间模式下的社交链接特殊样式 */
.dark-mode .social-link {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.dark-mode .social-link:hover {
  background: #ffffff;
  color: #667eea;
  box-shadow: 0 6px 20px rgba(255, 255, 255, 0.2);
}

/* uiverse.io 切换按钮样式 */
.toggle-cont {
  --primary: #54a8fc;
  --light: #d9d9d9;
  --dark: #121212;
  --gray: #414344;

  position: fixed;
  top: 30px;
  right: 30px;
  z-index: 1000;
  width: fit-content;
  height: 35px; /* 缩小高度 */
  border-radius: 9999px;
}

.toggle-cont .toggle-input {
  display: none;
}

.toggle-cont .toggle-label {
  --gap: 3px; /* 缩小间隙 */
  --width: 35px; /* 缩小宽度 */

  cursor: pointer;
  position: relative;
  display: inline-block;
  padding: 0.3rem; /* 缩小内边距 */
  width: calc((var(--width) + var(--gap)) * 2);
  height: 100%;
  background-color: var(--dark);
  border: 1px solid #777777;
  border-bottom: 0;
  border-radius: 9999px;
  box-sizing: content-box;
  transition: all 0.3s ease-in-out;
}

.toggle-label::before {
  content: "";
  position: absolute;
  z-index: -10;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% + 1.5rem);
  height: calc(100% + 1.5rem);
  background-color: var(--gray);
  border: 1px solid #777777;
  border-bottom: 0;
  border-radius: 9999px;
  transition: all 0.3s ease-in-out;
}

.toggle-label::after {
  content: "";
  position: absolute;
  z-index: -10;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background-image: radial-gradient(
    circle at 50% -100%,
    rgb(58, 155, 252) 0%,
    rgba(12, 12, 12, 1) 80%
  );
  border-radius: 9999px;
}

.toggle-cont .toggle-label .cont-icon {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--width);
  height: 35px; /* 匹配新的整体高度 */
  background-image: radial-gradient(
    circle at 50% 0%,
    #666666 0%,
    var(--gray) 100%
  );
  border: 1px solid #aaaaaa;
  border-bottom: 0;
  border-radius: 9999px;
  box-shadow: inset 0 -0.15rem 0.15rem var(--primary),
    inset 0 0 0.5rem 0.75rem var(--second);
  transition: transform 0.3s ease-in-out;
}

.cont-icon {
  overflow: clip;
  position: relative;
}

.cont-icon .sparkle {
  position: absolute;
  top: 50%;
  left: 50%;
  display: block;
  width: calc(var(--width) * 1px);
  aspect-ratio: 1;
  background-color: var(--light);
  border-radius: 50%;
  transform-origin: 50% 50%;
  rotate: calc(1deg * var(--deg));
  transform: translate(-50%, -50%);
  animation: sparkle calc(100s / var(--duration)) linear
    calc(0s / var(--duration)) infinite;
}

@keyframes sparkle {
  to {
    width: calc(var(--width) * 0.5px);
    transform: translate(2000%, -50%);
  }
}

.cont-icon .icon {
  width: 1.1rem;
  fill: var(--light);
}

.toggle-cont:has(.toggle-input:checked) {
  --checked: true;
}

@container style(--checked: true) {
  .toggle-cont .toggle-label {
    background-color: #41434400;
    border: 1px solid #3d6970;
    border-bottom: 0;
  }

  .toggle-cont .toggle-label::before {
    box-shadow: 0 1rem 2.5rem -2rem #0080ff;
  }

  .toggle-cont .toggle-label .cont-icon {
    overflow: visible;
    background-image: radial-gradient(
      circle at 50% 0%,
      #045ab1 0%,
      var(--primary) 100%
    );
    border: 1px solid var(--primary);
    border-bottom: 0;
    transform: translateX(calc((var(--gap) * 2) + 100%)) rotate(-225deg);
  }

  .toggle-cont .toggle-label .cont-icon .sparkle {
    z-index: -10;
    width: calc(var(--width) * 1.5px);
    background-color: #acacac;
    animation: sparkle calc(100s / var(--duration)) linear
      calc(10s / var(--duration)) infinite;
  }

  @keyframes sparkle {
    to {
      width: calc(var(--width) * 1px);
      transform: translate(5000%, -50%);
    }
  }
}

/* 兼容性样式 - 当不支持 @container 时的备用方案 */
.toggle-cont .toggle-input:checked + .toggle-label {
  background-color: #41434400;
  border: 1px solid #3d6970;
  border-bottom: 0;
}

.toggle-cont .toggle-input:checked + .toggle-label::before {
  box-shadow: 0 1rem 2.5rem -2rem #0080ff;
}

.toggle-cont .toggle-input:checked + .toggle-label .cont-icon {
  overflow: visible;
  background-image: radial-gradient(
    circle at 50% 0%,
    #045ab1 0%,
    var(--primary) 100%
  );
  border: 1px solid var(--primary);
  border-bottom: 0;
  transform: translateX(calc((var(--gap) * 2) + 100%)) rotate(-225deg);
}

.toggle-cont .toggle-input:checked + .toggle-label .cont-icon .sparkle {
  z-index: -10;
  width: calc(var(--width) * 1.5px);
  background-color: #acacac;
  animation: sparkle calc(100s / var(--duration)) linear
    calc(10s / var(--duration)) infinite;
}

/* 汉堡菜单样式 */
.hamburger-menu {
  position: fixed;
  top: 30px;
  left: 30px;
  width: 50px;
  height: 50px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  display: none; /* 桌面端默认隐藏 */
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  z-index: 1001;
  box-shadow: 0 4px 15px var(--shadow-medium);
  transition: all 0.3s ease;
}

/* 移动端显示汉堡菜单 */
@media screen and (max-width: 1200px) {
  .hamburger-menu {
    display: flex;
  }
}

/* 移动端遮罩层样式 */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  backdrop-filter: blur(2px);
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

/* 移动端遮罩层只在移动端显示 */
@media screen and (min-width: 1201px) {
  .sidebar-overlay {
    display: none;
  }
}

.hamburger-menu:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px var(--shadow-medium);
  background: var(--bg-tertiary);
}

.hamburger-menu span {
  width: 25px;
  height: 3px;
  background: var(--text-primary);
  border-radius: 2px;
  transition: all 0.3s ease;
  transform-origin: center;
}

/* 夜间模式下的汉堡菜单特殊样式 */
.dark-mode .hamburger-menu {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
}

.dark-mode .hamburger-menu:hover {
  background: var(--bg-tertiary);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.6);
  border-color: #667eea;
}

.dark-mode .hamburger-menu span {
  background: var(--text-primary);
}

.hamburger-menu.active .line-top {
  transform: rotate(45deg) translate(6px, 6px);
}

.hamburger-menu.active .line-center {
  opacity: 0;
  transform: scaleX(0);
}

.hamburger-menu.active .line-bottom {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* 预加载器夜间模式样式 */
.dark-mode #preloader {
  background-color: var(--bg-primary);
}

.dark-mode .loader3 span {
  background: linear-gradient(45deg, #667eea, #764ba2, #f093fb);
  box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
}

/* 夜间模式下的内容区域优化 */
.dark-mode .content-wrapper {
  background-color: var(--bg-primary);
}

/* 夜间模式下的渐变文字优化 */
.dark-mode .gradient-text {
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
}
</style>
